import React, {useState} from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  IconButton,
  Typography,
} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {
  CustomButton,
  Layout,
  PageHeader,
  ServicesFormHeader,
} from '../../components';
import CloseIcon from '@mui/icons-material/Close';
import {addPdfReport} from '../../actions';
import {SERVICES_HEADER_FIELDS} from '../../constants/servicesHeaderFields';
import ESCALATOR_SERVICES_FIELDS from '../../constants/escalatorServicesFields';
import imageCompression from 'browser-image-compression';

const Title = ({text, size = '14px', mt = '1.5rem'}) => (
  <Typography fontSize={size} fontWeight="bold" m="1.5rem 0 0.5rem" mt={mt}>
    {text.toUpperCase()}
  </Typography>
);

const incrementSerialNumber = () => {
  const currentYear = moment().year();
  let currentSerial = localStorage.getItem('serialNo') || '001';

  let nextSerial = parseInt(currentSerial, 10) + 1;
  if (nextSerial > 999) nextSerial = 1;

  const newSerial = nextSerial.toString().padStart(3, '0');
  const fullSerial = `${newSerial}-${currentYear}`;

  localStorage.setItem('serialNo', newSerial);

  return fullSerial;
};

const ChecksGroup = ({firstIndex}) => (
  <>
    <CheckBoxField index={firstIndex} text="Lart majtas" />
    <CheckBoxField index={firstIndex + 1} text="Lart djathas" />
    <CheckBoxField index={firstIndex + 2} text="Poshtë majtas" />
    <CheckBoxField index={firstIndex + 3} text="Poshtë djathas" />
  </>
);

const CheckBoxField = ({index, text, defaultChecked = true}) => {
  const name = `check${index}`;

  return (
    <FormControlLabel
      name={name}
      control={<Checkbox defaultChecked={defaultChecked} />}
      label={text}
    />
  );
};

const InputWithCheck = ({index}) => (
  <Box display="flex" alignItems="center">
    <CheckBoxField index={index} text="" defaultChecked={false} />
    <TextField
      name={`check${index}Text`}
      inputProps={{maxLength: 10}}
      size="small"
    />
  </Box>
);

const AddEscalatorReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {elevatorId, elevatorDetails} = location.state || {};
  const {pdfLoading, addPdfLoading} = useSelector(s => s.pdfReports);

  console.log('current');
  const onBack = () => navigate(-1);

  const [images, setImages] = useState([]);

  const onSubmit = e => {
    e.preventDefault();
    const payload = {date: moment(e.target.date.value).format('DD/MM/YYYY')};
    const serialNumber = incrementSerialNumber();

    if (images.length > 0) {
      payload.images = images;
    }

    Object.entries(e.target.elements).map(([key, value]) => {
      if (SERVICES_HEADER_FIELDS.includes(key)) {
        return (payload[key] = value.value);
      }
      if (ESCALATOR_SERVICES_FIELDS.includes(key)) {
        return (payload[key] = value.checked);
      }
      return null;
    });
    dispatch(addPdfReport(payload, 'ESCALATOR'));
  };

  const handleImageChange = e => {
    const files = Array.from(e.target.files);
    const imageFiles = files.map(async file => {
      const options = {
        maxSizeMB: 20, 
        maxWidthOrHeight: 2026, 
        useWebWorker: true, 
      };

      try {
        const compressedFile = await imageCompression(file, options);

        const reader = new FileReader();
        return new Promise(resolve => {
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(compressedFile);
        });
      } catch (error) {
        console.error('Image compression error:', error);
        return null;
      }
    });

    Promise.all(imageFiles).then(base64Images => {
      setImages(prevImages => [
        ...prevImages,
        ...base64Images.filter(image => image !== null),
      ]);
    });

    e.target.value = '';
  };

  const handleImageRemove = index => {
    setImages(prevImages => prevImages.filter((_, i) => i !== index));
  };

  return (
    <Layout>
      <PageHeader onBack={onBack} title="Add Escalator Report" />
      <form onSubmit={onSubmit}>
        <ServicesFormHeader elevatorDetails={elevatorDetails} />
        <Box mx="2rem">
          <Title size="18px" text="ÇDO MUAJ" />
          <Box display="flex" gap="1.5rem">
            <Box flex={1}>
              <Title text="Pastrimi" />
              <CheckBoxField
                index={1}
                text="Pastrimi i pjesës së epërme dhe të poshtme të dhomës makinerike"
              />
              <Title text="Paneli i krehërve" />
              <CheckBoxField index={5} text="Nuk ka dhëmbë të thyer" />
              <CheckBoxField
                index={6}
                text="Distanca në mes krehërve të shkallës dhe kanaleve të tyre (ku përshtatet shkelësja) të jetë minimum 6mm"
              />
              <CheckBoxField
                index={7}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
            </Box>
            <Box flex={1}>
              <Title text="Distanca në mes shkelësve dhe anësoreve" />
              <CheckBoxField index={2} text="Max 4mm" />
              <CheckBoxField
                index={3}
                text="Shuma e distancës e matur në të dyja anët e shkallës nuk duhet të kaloj 7mm"
              />
              <CheckBoxField
                index={4}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
              <Title text="Shkelëset (shkalla)" />
              <CheckBoxField index={8} text="Nuk ka dhëmbë të thyer" />
              <CheckBoxField
                index={9}
                text="Shkelësja nuk duhet të ketë tolerancë të lëvizjes anësore më shumë se 0.4mm, (lëvizja në boshtin e shkelëses në raport me shkelësin)"
              />
              <CheckBoxField
                index={10}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
            </Box>
          </Box>
          <Title size="18px" text="çdo dy muaj" />
          <Box display="flex" gap="1.5rem">
            <Box flex={1}>
              <Title text="Rollerët (zinxhiri) i shkelësve" />
              <CheckBoxField
                index={11}
                text="Kontrollo tensionerin e zinxhirit të shkelësve"
              />
              <CheckBoxField
                index={12}
                text="Vajosja rollerëve (zinxhiri) i shkelësve"
              />
              <CheckBoxField
                index={13}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
              <Title text="Zinxhiri ngasës" />
              <CheckBoxField
                index={17}
                text="Lëvizja (lirimi) e zinxhirit ngasës është ndërmjet 10-15mm gjatë lëvizjes"
              />
              <CheckBoxField index={18} text="Vajosja e zinxhirit ngasës" />
              <CheckBoxField
                index={19}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
            </Box>
            <Box flex={1}>
              <Title text="Zinxhiri i ngasjes së handrail (parmakut të gomës)" />
              <CheckBoxField
                index={14}
                text="Lëvizja (lirimi) i zinxhirit të ngasjes së handrail është ndërmjet 10-15mm në lëvizje të lirë"
              />
              <CheckBoxField
                index={15}
                text="Vajosja zinxhirin i ngasjes së handrail (parmakut të gomëes)"
              />
              <CheckBoxField
                index={16}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
              <Title text="Njësia ngasëse (motori)" />
              <CheckBoxField
                index={20}
                text="Kontrollo nivelin e vajit të motorit"
              />
              <CheckBoxField index={21} text="Nuk ka rrjedhje në motor" />
              <CheckBoxField
                index={22}
                text="Nuk ka zhurmë të jashtëzakonshme në motor"
              />
              <CheckBoxField
                index={23}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
            </Box>
          </Box>
          <Title size="18px" text="çdo muaj" />
          <Title text="qarqet e sigurisë" />
          <Box display="flex" gap="1.5rem">
            <Box flex={1}>
              <Title size="12px" text="kontakti i krehërve" />
              <ChecksGroup firstIndex={24} />
              <Title
                size="12px"
                text="Kontakti i sigurisë në rast të devijimit së shkelëses"
              />
              <CheckBoxField index={28} text="Lartë" />
              <CheckBoxField index={29} text="Poshtë" />
              <Title size="12px" text="Kontakti hyrës i parmakut të gomës" />
              <ChecksGroup firstIndex={36} />
              <Title size="12px" text="Kontakti i zinxhirit të shkelëseve" />
              <CheckBoxField index={42} text="Poshtë majtas" />
              <CheckBoxField index={43} text="Poshtë djathtas" />
            </Box>
            <Box flex={1}>
              <Title size="12px" text="kontakti i anësoreve" />
              <ChecksGroup firstIndex={30} />
              <Title
                size="12px"
                text="Kontakti në rast të mbingarkimit (vetëm për tapeta lëvizëse)"
              />
              <CheckBoxField index={34} text="Lartë" />
              <CheckBoxField index={35} text="Poshtë" />
              <Title size="12px" text="Kontakti i zinxhirit ngasës" />
              <CheckBoxField index={40} text="Zinxhiri 1" />
              <CheckBoxField index={41} text="Zinxhiri 2 (nëse ka)" />
              <Title size="12px" text="Qarqe të tjera të sigurisë" />
              <InputWithCheck index={44} />
              <InputWithCheck index={45} />
              <InputWithCheck index={46} />
              <InputWithCheck index={47} />
            </Box>
          </Box>
          <Title text="Distanca e frenimit" />
          <CheckBoxField index={48} text="Kontrollo bobinën e frenave" />
          <CheckBoxField
            index={49}
            text="Distanca e ndaljes (frenimit) në lëvizje të lirë (max 300mm)"
          />
          <CheckBoxField
            index={50}
            text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
          />
          <Title size="18px" text="çdo tre muaj" />
          <Title text="Handrail (parmaku i gomës)" />
          <CheckBoxField
            index={51}
            text="Kontrollo tensionin (shtrëngimin e parmakut)"
          />
          <CheckBoxField index={52} text="Pastroni parmakun" />
          <CheckBoxField
            index={53}
            text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
          />
          <Title size="18px" text="çdo vit" />
          <Title text="Boshti kryesor ngasës" />
          <Box display="flex">
            <Box flex={1}>
              <CheckBoxField
                index={54}
                text="Kontakti nëse boshti ngasës i parmakut ka funksionim normal"
              />
              <CheckBoxField
                index={57}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
            </Box>
            <Box flex={1}>
              <Title
                size="12px"
                mt="0"
                text="Vajosja boshtin e njësisë ngasëse"
              />
              <CheckBoxField index={55} text="Majtas" />
              <CheckBoxField index={56} text="Djathtas" />
            </Box>
          </Box>
          <Title text="Boshti ngasës i handrail (parmakut të gomës)" />
          <Box display="flex">
            <Box flex={1}>
              <CheckBoxField
                index={58}
                text="Kontrollo nëse boshti ngasës i parmakut ka funksionim normal"
              />
              <CheckBoxField
                index={61}
                text="Nëse kërkohet rregullimi (kalibrimi) a është bërë?"
              />
            </Box>
            <Box flex={1}>
              <Title size="12px" mt="0" text="Vajosja boshtin" />
              <CheckBoxField index={59} text="Majtas" />
              <CheckBoxField index={60} text="Djathtas" />
            </Box>
          </Box>
        </Box>
        <Title text="Shtoni foto" />
        <Box display="flex" justifyContent="center" mx="2rem">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            style={{marginBottom: '1rem'}}
          />
        </Box>
        {images.length > 0 && (
          <Box
            display="flex"
            flexWrap="wrap"
            gap="10px"
            justifyContent="center"
            my="1rem">
            {images.map((img, idx) => (
              <Box key={idx} position="relative">
                <img
                  src={img}
                  alt={`upload-${idx}`}
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
                <IconButton
                  onClick={() => handleImageRemove(idx)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 1)',
                    },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box display="flex" justifyContent="center" m="2rem">
          <CustomButton
            sx={{width: 'max(300px, 40%)'}}
            type="submit"
            loading={addPdfLoading}>
            Add Report
          </CustomButton>
        </Box>
      </form>
    </Layout>
  );
};

export default AddEscalatorReport;
